import moment from 'moment'
import config from 'config'
import { createSelector } from 'reselect'
import createCachedSelector from 're-reselect'
import { emptyObj } from 'util/objects'
import { emptyArr, sortByKey } from 'util/arrays'
import { selectIsInInbox } from 'selectors/location'
import { selectAccount } from 'selectors/app'
import {
  selectCurrentUserIsAdmin,
  selectCurrentUserIsAdminOrOwner,
} from 'ducks/currentUser/selectors/base'
import { selectIsBillingPage } from 'subapps/settings/selectors'
import { selectFlag } from 'ducks/flags'
import { createBasicSelector } from 'util/redux'
import { selectRequestByType } from 'ducks/requests/selectors'
import { HIDE_IMPROVEMENT_IDEA } from 'ducks/flags/flagNames'
import { selectIsBootstrapTypedInStates } from 'selectors/bootstrap/makeSelectIsBootstrapTypesLoaded'
import { diff, toDate } from 'util/date'
import storage from 'util/storage'

import selectBase from './selectBase'
import { UPDATE_SUBSCRIPTION } from '../types'

const ACTIVE_PRODUCT_STATES = ['paid', 'trial']

export const selectHasLegacyKb = createSelector(
  selectBase,
  billing => billing.has_legacy_kb
)

export const selectIsBillingDataLoaded = createSelector(
  selectBase,
  base => !!base.isLoaded
)

export const selectIsBillingDataLoading = createSelector(
  selectBase,
  base => !!base.isLoading
)

export const selectIsSaving = createSelector(
  selectBase,
  base => !!base.isSaving
)

export const selectIsCanceling = createSelector(
  selectBase,
  base => !!base.isCanceling
)

export function selectProduct(state, product) {
  const billing = selectBase(state)
  if (['kb', 'inbox'].indexOf(product) === -1)
    throw new Error(`Unknown product '${product}'`)
  return billing[product] || emptyObj
}

export const selectInboxProduct = createSelector(
  selectBase,
  base => base.inbox || emptyObj
)

export const selectKbProduct = createSelector(
  selectBase,
  base => base.kb || emptyObj
)

export const selectKbFeatures = createSelector(
  selectKbProduct,
  kbProduct => kbProduct.features || emptyObj
)

export const selectProductFeatures = createSelector(
  selectProduct,
  product => product.features || emptyObj
)

export const selectMaxKbs = createSelector(
  selectKbFeatures,
  kbFeatures => kbFeatures.max_kbs
)

export const selectMaxArticles = createSelector(
  selectKbFeatures,
  kbFeatures => kbFeatures.max_articles
)

export const selectDaysLeftBeforeExpiring = createSelector(
  selectProduct,
  product =>
    product ? moment(product.expires_at).diff(moment(), 'minutes') / 1440 : null
)

export const selectIsFreemium = createSelector(
  selectProduct,
  plan => ['v9-standard', 'v9-standard-annual'].indexOf(plan.plan) !== -1
)

export const selectIsTrial = createCachedSelector(
  selectProduct,
  selectIsFreemium,
  (plan, isFreemium) => {
    return plan?.state === 'trial' && !isFreemium
  }
)((_state, product) => product || 'unknown')

export const selectShowTrialMessage = createSelector(
  selectIsTrial,
  selectDaysLeftBeforeExpiring,
  selectIsInInbox,
  selectFlag,
  (isTrial, trialDaysLeft, isInInbox, flags) =>
    isInInbox &&
    isTrial &&
    trialDaysLeft > 0 &&
    trialDaysLeft <= config.trialShowNoticeBannerDaysLeft &&
    flags('app-notice-trialinfo') !== true
)

export const selectHasCompletedTrial = createBasicSelector(
  selectBase,
  billing => !!billing?.inbox?.has_completed_trial
)

export const selectIsProductActive = createSelector(selectProduct, product =>
  ACTIVE_PRODUCT_STATES.includes(product.state)
)

export const selectIsInboxActive = createSelector(
  selectBase,
  base => base.inbox && ACTIVE_PRODUCT_STATES.includes(base.inbox.state)
)

export const selectIsKbActive = createSelector(
  selectBase,
  base => base.kb && ACTIVE_PRODUCT_STATES.includes(base.kb.state)
)

export const selectPlans = createSelector(selectBase, base => base.plans || [])

export const selectPlansByProduct = createCachedSelector(
  (_state, product) => product,
  selectPlans,
  (product, plans) => plans.filter(p => p.product === product)
)((_state, product) => product || 'unknown')

export const selectPlansByProductDropdown = createCachedSelector(
  selectPlansByProduct,
  selectProduct,
  (plans, product) =>
    plans.filter(p => p.active || p.planId === product.plan).map(p => ({
      id: p.planId,
      name: `${!p.active ? 'Legacy ' : ''}${p.name}`,
    }))
)((_state, product) => product || 'unknown')

export const selectHasPlans = createSelector(selectPlans, plans => {
  return plans.length !== 0
})

export const selectCurrentPlanByProduct = createCachedSelector(
  (_state, product) => product,
  selectBase,
  selectPlans,
  selectIsBillingDataLoaded,
  (product, base, allPlans, isBillingDataLoaded) => {
    if (!base[product] || !isBillingDataLoaded || allPlans.length === 0)
      return emptyObj

    const plans = allPlans.filter(p => p.product === product)
    const currentPlan = plans.find(p => p.planId === base[product].plan)
    if (
      base[product].state === 'paid' ||
      base[product].state === 'trial' ||
      currentPlan.active
    ) {
      return {
        ...base[product],
        id: product,
        plan: currentPlan,
      }
    }

    const eligiblePlans = plans.filter(p => p.active)
    const defaultPlan = sortByKey(eligiblePlans, 'monthlyPrice')[0]
    return {
      ...base[product],
      id: product,
      plan: defaultPlan,
    }
  }
)((_state, product) => product || 'unknown')

export const selectCurrentKb = state => {
  return selectCurrentPlanByProduct(state, 'kb')
}

export const selectCurrentInbox = state => {
  return selectCurrentPlanByProduct(state, 'inbox')
}

export const selectCancelsAt = createSelector(
  selectBase,
  base => base.cancels_at
)

export const selectIsPendingCancellation = createSelector(
  selectCancelsAt,
  cancelsAt => !!cancelsAt
)

export const selectShowPendingCancellation = createSelector(
  selectIsPendingCancellation,
  selectIsBillingPage,
  selectFlag,
  (isPendingCancellation, isOnBillingPage, flags) => {
    return (
      isPendingCancellation &&
      isOnBillingPage &&
      flags &&
      flags('app-notice-pendingcancellation') !== true
    )
  }
)

export const selectCurrentBilling = createSelector(
  selectBase,
  selectCurrentInbox,
  selectCurrentKb,
  selectIsSaving,
  selectIsPendingCancellation,
  (base, inbox, kb, isSaving, isPendingCancellation) => ({
    state: base.state,
    inbox: inbox !== emptyObj ? inbox.plan : null,
    inboxState: inbox !== emptyObj ? inbox.state : null,
    agentCount: inbox !== emptyObj ? inbox.quantity : null,
    kb: kb !== emptyObj ? kb.plan : null,
    kbState: kb !== emptyObj ? kb.state : null,
    cycle: base.billing_cycle,
    trials: {
      inbox: inbox !== emptyObj ? inbox.state === 'trial' : null,
      kb: kb !== emptyObj ? kb.state === 'trial' : null,
    },
    cancels_at: base.cancels_at,
    saving: isSaving,
    canceling: base.isCanceling,
    canceled: isPendingCancellation,
  })
)

export const selectCurrentBillingCycle = createSelector(
  selectBase,
  base => base.billing_cycle
)

export const selectIsStartingTrial = createSelector(
  selectBase,
  base => base.isStartingTrial === true
)

export const selectIsAccountExpired = createSelector(selectBase, base => {
  return base.state === 'expired'
})

export const selectIsBillingEstimateAvailable = createBasicSelector(
  selectBase,
  selectCurrentInbox,
  selectCurrentKb,
  (base, currentInbox, currentKb) => {
    const { state } = base
    const { state: inboxState } = currentInbox
    const { state: kbState } = currentKb
    const includeAccountStates = ['open', 'expired', 'closed']
    const excludePlanStates = [null, 'trial']
    if (
      state &&
      includeAccountStates.includes(state) &&
      !(
        excludePlanStates.includes(kbState) ||
        excludePlanStates.includes(inboxState)
      )
    ) {
      return true
    }
    return false
  }
)

export const selectIsAccountClosed = createSelector(selectBase, base => {
  return base.state === 'closed'
})

export const selectIsUpdatingCard = createSelector(
  selectBase,
  base => base.isUpdatingCard
)

export const selectInvoiceBase = createSelector(
  selectBase,
  base => base.invoices || emptyObj
)

export const selectIsInvoicesLoaded = createSelector(
  selectInvoiceBase,
  base => !!base.isLoaded
)

export const selectInvoices = createSelector(
  selectInvoiceBase,
  base => base.data || emptyArr
)

export const selectInvoiceExcludingDrafts = createSelector(
  selectInvoices,
  invoices => invoices.filter(invoice => invoice.status !== 'draft')
)

export const selectBillingInformationBase = createSelector(
  selectBase,
  base => base.information || emptyObj
)

export const selectIsBillingInformationLoaded = createSelector(
  selectBillingInformationBase,
  base => !!base.isLoaded
)

export const selectBillingInformation = createSelector(
  selectBillingInformationBase,
  base => base.data || emptyObj
)

export const selectHasBillingInformationError = createSelector(
  selectBillingInformationBase,
  base => !!base.hasError
)

export const selectCardError = createSelector(
  selectBase,
  base => base.updateCardError
)

export const selectShowCardErrorModal = createSelector(
  selectBase,
  base => base.showCardErrorModal
)

export const selectCreditCards = createSelector(
  selectBase,
  base => base.cards || emptyArr
)

export const selectPrimaryCreditCard = createSelector(
  selectCreditCards,
  // Kevin Rademan (2019-03-29)
  // Note we dont currently have multiple cards in the backend, so there
  // will always only be 1 card (if you've addedf a card) and that card
  // will be your primary. Im keeping the interface an array incase down
  // the line we want to support having primary and backup credit cards
  // (common for most business with business critical software)
  cards => (cards.length > 0 ? cards[0] : null)
)

export const selectIsCreditCardExpiringSoon = createSelector(
  selectPrimaryCreditCard,
  card => {
    if (!card) return false
    const { expiryMonth, expiryYear } = card
    const ccDate = moment(`${expiryYear}-${expiryMonth}-01`, 'YYYY-MM-DD').add(
      1,
      'months'
    )
    const currentDate = moment()
    const expiresIn = ccDate.diff(currentDate, 'days')
    if (expiresIn >= 0 && expiresIn <= 42) return true
    // When expires in is < 0, then we return false becaues its already expired
    return false
  }
)

export const selectShowCreditCardExpireSoon = createSelector(
  selectIsBillingPage,
  selectIsCreditCardExpiringSoon,
  selectCurrentUserIsAdmin,
  selectFlag,
  selectPrimaryCreditCard,
  (
    isBillingPage,
    isCreditCardExpiringSoon,
    currentUserIsAdmin,
    flags,
    creditCard
  ) => {
    if (!creditCard) return false
    const { expiryMonth, expiryYear } = creditCard
    const flagName = `has_seen_credit_card_expire_soon_${expiryYear}_${expiryMonth}`
    return (
      currentUserIsAdmin &&
      !isBillingPage &&
      isCreditCardExpiringSoon &&
      flags(flagName) !== true
    )
  }
)

export const selectIsCreditCardExpired = createSelector(
  selectPrimaryCreditCard,
  () => new Date().getFullYear(),
  () => new Date().getMonth() + 1,
  (card, currentYear, currentMonth) => {
    if (!card) return false
    if (card.expiryYear < currentYear) return true
    if (card.expiryYear > currentYear) return false
    if (card.expiryMonth < currentMonth) return true
    return false
  }
)

export const selectHasLastBillingFailed = createSelector(
  selectBase,
  base => !!base.hasLastBillingFailed
)

export const selectShowLastBillingFailed = createSelector(
  selectHasLastBillingFailed,
  selectCurrentUserIsAdmin,
  (hasLastBillingFailed, currentUserIsAdmin) =>
    currentUserIsAdmin && hasLastBillingFailed
)

export const selectShowCreditCardExpired = createSelector(
  selectIsBillingPage,
  selectIsCreditCardExpired,
  selectCurrentUserIsAdmin,
  (isBillingPage, isCreditCardExpired, currentUserIsAdmin) =>
    currentUserIsAdmin && !isBillingPage && isCreditCardExpired
)

export const selectCcLast4 = createSelector(
  selectPrimaryCreditCard,
  card => card.last4
)

export const selectCcExp = createSelector(
  selectPrimaryCreditCard,
  card => `${card.expiryMonth}/${card.expiryYear}`
)

export const selectBillingAccount = createSelector(
  selectBase,
  base => base.account || emptyObj
)

export const selectUpcomingInvoice = createSelector(
  selectBillingAccount,
  billingAccount => billingAccount.upcomingInvoice || emptyObj
)

export const selectIsBillingAccountActive = createSelector(
  selectIsAccountExpired,
  selectIsAccountClosed,
  selectIsInboxActive,
  selectIsBillingDataLoaded,
  (isAccountExpired, isAccountClosed, isInboxActive, isBillingLoaded) => {
    if (!isBillingLoaded) return true
    if (isAccountExpired || isAccountClosed) return false
    if (!isInboxActive) return false
    return true
  }
)

export const selectShowExpiredBilling = createSelector(
  selectIsBillingAccountActive,
  selectCurrentUserIsAdminOrOwner,
  (isBillingAccountActive, isManager) => {
    return !isBillingAccountActive && isManager
  }
)

export const selectIsSubscriptionActive = createSelector(
  selectIsBillingAccountActive,
  selectIsPendingCancellation,
  (isBillingAccountActive, isPendingCancellation) => {
    if (isPendingCancellation) return false
    return isBillingAccountActive
  }
)

export const selectUpcomingInvoiceItems = createSelector(
  selectUpcomingInvoice,
  invoice => invoice.lines || []
)

export const selectBillingEstimate = createSelector(
  selectBillingAccount,
  selectUpcomingInvoice,
  selectUpcomingInvoiceItems,
  selectIsBillingAccountActive,
  selectIsSubscriptionActive,
  (
    billingAccount,
    upcomingInvoice,
    upcomingInvoiceLines,
    isBillingAccountActive,
    isSubscriptionActive
  ) => {
    return {
      isAccountActive: isBillingAccountActive,
      isSubscriptionActive,
      hasInvoice: !!upcomingInvoice.id,
      nextBillDate: upcomingInvoice.nextPaymentAttempt,
      subscriptionCharges: upcomingInvoiceLines
        .filter(item => !item.proration)
        .reduce((total, item) => item.amount + total, 0),
      prorataCharges: upcomingInvoiceLines
        .filter(item => item.proration)
        .reduce((total, item) => item.amount + total, 0),
      creditBalance: billingAccount.creditBalance,
      total: upcomingInvoice.amountDue,
      currency: upcomingInvoice.currency,
    }
  }
)

export const selectBillingUsage = createBasicSelector(
  selectBase,
  base => base.usage || emptyObj
)

export const selectBillingPendingSubscription = createBasicSelector(
  selectBase,
  base => base.pendingSubscription || emptyObj
)

export const selectBillingProvider = createBasicSelector(
  selectBase,
  base => base.provider || 'stripe'
)

export const selectBillingStartTrialDurationDays = createBasicSelector(
  selectBase,
  base => base.startTrialDurationDays
)

export const selectBillingProviderSettings = createSelector(
  selectBillingProvider,
  provider => ({
    hasCardSupport: provider === 'stripe',
    requireExternalApproval: provider === 'shopify',
    supportBillingEstimateCard: provider === 'stripe',
    supportBillingPaymentMethodCard: provider === 'stripe',
    supportBillingInformation: provider === 'stripe',
    supportBillingInvoices: provider === 'stripe',
    supportBillingActivity: provider === 'stripe',
    supportAllBillingCycles: provider === 'stripe',
  })
)

export function selectTotalArticlesFromBillingUsage(state) {
  const { kb: { articles = 'unlimited' } = {} } = selectBillingUsage(state)
  return articles
}

export const selectHasKbProPlan = createSelector(
  selectKbProduct,
  product => product.plan === 'pro'
)

export const selectIsPaidAccount = createSelector(
  selectBase,
  base => !!base.isPaid
)

export const selectTotalCannedRepliesFromBillingUsage = createBasicSelector(
  selectBillingUsage,
  billingUsage => {
    const { inbox: { cannedReplies = 0 } = {} } = billingUsage
    return cannedReplies
  }
)

export const selectUpdateSubscriptionRequest = state =>
  selectRequestByType(state, UPDATE_SUBSCRIPTION)

export const selectBillingExternalAccountId = createBasicSelector(
  selectBase,
  base => base.externalAccountId
)

export const selectBillingExternalSubscriptionId = createBasicSelector(
  selectBase,
  base => base.externalSubscriptionId
)

export const selectCurrentInboxPlan = createSelector(
  selectInboxProduct,
  selectPlans,
  (product, plans) => {
    return plans.find(p => p.product === 'inbox' && p.planId === product.plan)
  }
)

export const selectCurrentKbPlan = createSelector(
  selectKbProduct,
  selectPlans,
  (product, plans) => {
    return plans.find(p => p.product === 'kb' && p.planId === product.plan)
  }
)

export const selectCurrentTotalPerCycle = createSelector(
  selectIsBillingDataLoaded,
  selectCurrentInboxPlan,
  selectCurrentKbPlan,
  selectCurrentBillingCycle,
  selectBillingUsage,
  (loaded, inboxPlan, kbPlan, billingCycle, usage) => {
    if (!loaded) return undefined
    const inboxQuantity =
      inboxPlan.pricingModel === 'flat' ? 1 : usage.inbox.users
    const kbQuantity = kbPlan.pricingModel === 'flat' ? 1 : usage.inbox.kbs
    const inboxPeriodPrice = inboxPlan[`${billingCycle}Price`]
    const kbPeriodPrice = kbPlan[`${billingCycle}Price`]
    if (inboxPeriodPrice === undefined) {
      throw new Error('Invalid inbox price')
    }
    if (kbPeriodPrice === undefined) {
      throw new Error('Invalid kb price')
    }

    const inboxSubTotal = inboxQuantity * inboxPeriodPrice
    const kbSubTotal = kbQuantity * kbPeriodPrice

    return inboxSubTotal + kbSubTotal
  }
)

export const selectShowImprovementIdeaBar = createSelector(
  selectFlag,
  selectAccount,
  selectIsPaidAccount,
  (flag, account, isPaidAccount) => {
    if (
      isPaidAccount &&
      diff('weeks', account.created_at, new Date()) > 2 &&
      !flag(HIDE_IMPROVEMENT_IDEA)
    ) {
      const expirationDate = storage.get('has_seen_improvement_idea')
      return expirationDate ? toDate(expirationDate) < new Date() : true
    }
    return false
  }
)

export const selectShowAnnualOffer = createSelector(
  selectIsSubscriptionActive,
  selectCurrentUserIsAdminOrOwner,
  selectCurrentBillingCycle,
  selectBillingProvider,
  state => selectIsTrial(state, 'inbox'),
  state => selectIsBootstrapTypedInStates(state, ['billing'], ['LOADED']),
  (
    isSubscriptionActive,
    isManager,
    billingCycle,
    billingProvider,
    isTrial,
    isBillingBootstrapped
  ) => {
    const daysDiff = diff('days', '2023-05-30T00:00:00', new Date())
    return (
      isBillingBootstrapped &&
      billingProvider === 'stripe' &&
      isSubscriptionActive &&
      !isTrial &&
      isManager &&
      billingCycle !== 'annual' &&
      daysDiff <= 14
    )
  }
)
